import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { ChevronDown, User, Users } from "lucide-react";
import { ViewToggle } from "@/components/common/ViewToggle";
import { useTranslation } from "react-i18next";
import { useTasksFilter, TasksSortField } from "@/contexts/TasksFilterContext";

export const TasksViewOptions = () => {
	const { t } = useTranslation();
	const { sortField, setSortField, sortOrder, setSortOrder, viewMode, setViewMode, ownershipFilter, setOwnershipFilter } = useTasksFilter();

	const getSortLabel = () => {
		switch (sortField) {
			case "name": return t("sortBar.name");
			case "dateCreated": return t("sortBar.dateCreated");
			case "dueDate": return t("tasks.list.dueDate", "Due Date");
			case "priority": return t("tasks.list.priority", "Priority");
			default: return t("sortBar.sortBy");
		}
	};

	const handleToggleSortOrder = () => {
		setSortOrder(sortOrder === "asc" ? "desc" : "asc");
	};

	return (
		<div className="flex justify-between items-center mb-4 bg-white dark:bg-gray-800 p-3 rounded-sm shadow-sm">
			<div className="flex items-center gap-2">
				<span className="text-sm font-medium text-gray-500 dark:text-gray-400">{t("sortBar.sortBy")}</span>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant="outline" size="sm" className="flex items-center gap-1">
							{getSortLabel()}
							<ChevronDown className="h-4 w-4" />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="start">
						<DropdownMenuItem onClick={() => setSortField("name")}>{t("sortBar.name")}</DropdownMenuItem>
						<DropdownMenuItem onClick={() => setSortField("dateCreated")}>{t("sortBar.dateCreated")}</DropdownMenuItem>
						<DropdownMenuItem onClick={() => setSortField("dueDate")}>{t("tasks.list.dueDate", "Due Date")}</DropdownMenuItem>
						<DropdownMenuItem onClick={() => setSortField("priority")}>{t("tasks.list.priority", "Priority")}</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
				<Button
					variant="outline"
					size="sm"
					onClick={handleToggleSortOrder}
					className="ml-1"
				>
					{sortOrder === "asc" ? t("sortBar.ascending") : t("sortBar.descending")}
				</Button>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant="outline" size="sm" className="flex items-center gap-1 ml-2">
							{ownershipFilter === 'my-tasks' ? (
								<><User className="h-4 w-4 mr-1" />{t("tasks.ownership.myTasks", "My Tasks")}</>
							) : ownershipFilter === 'assigned-to-me' ? (
								<><User className="h-4 w-4 mr-1" />{t("tasks.ownership.assignedToMe", "Assigned to Me")}</>
							) : (
								<><Users className="h-4 w-4 mr-1" />{t("tasks.ownership.all", "All")}</>
							)}
							<ChevronDown className="h-4 w-4" />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="start">
						<DropdownMenuItem onClick={() => setOwnershipFilter('my-tasks')}>
							<User className="h-4 w-4 mr-2" />
							{t("tasks.ownership.myTasks", "My Tasks")}
						</DropdownMenuItem>
						<DropdownMenuItem onClick={() => setOwnershipFilter('assigned-to-me')}>
							<User className="h-4 w-4 mr-2" />
							{t("tasks.ownership.assignedToMe", "Assigned to Me")}
						</DropdownMenuItem>
						<DropdownMenuItem onClick={() => setOwnershipFilter('all')}>
							<Users className="h-4 w-4 mr-2" />
							{t("tasks.ownership.all", "All")}
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>
			<ViewToggle
				viewMode={viewMode}
				onViewModeChange={setViewMode}
				label={t("sortBar.view")}
				allowedModes={["card", "list", "kanban"]}
			/>
		</div>
	);
};
